import { UseFormSetValue }  from 'react-hook-form';
import ApiCollection        from '@widesk-core/models/ApiCollection';
import FileCopyModel        from '@/models/services/website/FileCopyModel';
import FileModel            from '@/models/services/website/FileModel';
import FileTypeModel        from '@/models/services/website/FileTypeModel';
import PagedCollection      from '@widesk-core/models/PagedCollection';
import React                from 'react';
import StandModel           from '@/models/services/website/StandModel';
import StandPageModel       from '@/models/services/website/StandPageModel';
import StandPageValidationStatusModel from '@/models/services/website/StandPageValidationStatusModel';
import useFetch             from '@widesk-core/hooks/useFetch';
import { getIdFromUrn }     from '@widesk-core/tools/identifier';
import { FormValues }       from './standFormTypes';
import type { ModelFile }   from './standFormTypes';
import { selectStandPageToEdit } from './standPageSelection';

export const useStandPageFiles = (
	stand: StandModel,
	activeStandPage: StandPageModel | null,
	setValue: UseFormSetValue<FormValues>,
	setActiveStandPage: (page: StandPageModel | null) => void,
	validationStatuses: StandPageValidationStatusModel[],
) => {

	// ─── Fetch des types de fichiers ─────────────────────────────────────────────

	const { data: typeCollection, isLoading: typeCollectionLoading } = useFetch(async () => {
		const collection = await new ApiCollection(FileTypeModel)
			.setFilter('reference', ['stand_page_logo', 'stand_page_image'])
			.list();
		return collection.models;
	}, true);

	const existingLogoRef    = React.useRef<ModelFile | null>(activeStandPage?.getFiles('stand_page_logo').at(0) ?? null);
	const existingGalleryRef = React.useRef<ModelFile[]>(activeStandPage?.getFiles('stand_page_image') ?? []);

	// ─── Sauvegarde des fichiers ─────────────────────────────────────────────────

	const saveFiles = async (
		formData: FormValues,
		currentStandPage: StandPageModel,
		existingLogo: ModelFile | null,
		existingGallery: ModelFile[],
		needsNewStandPage: boolean,
	) => {
		
		const standPageLogoType  = typeCollection?.find(m => m.reference === 'stand_page_logo')  as FileTypeModel;
		const standPageImageType = typeCollection?.find(m => m.reference === 'stand_page_image') as FileTypeModel;

		if (needsNewStandPage) {
			// 1 - Copier les fichiers existants vers le nouveau standPage (côté serveur, sans téléchargement)
			const filesToCopy: Promise<unknown>[] = [];

			if (existingLogo && formData.existingLogoUrl !== null && !formData.logo) {
				filesToCopy.push(new FileCopyModel().patch({ file: existingLogo['@urn'], sourceUrn: currentStandPage.urn, type: standPageLogoType?.urn }));
			}
			existingGallery
				.filter(f => formData.existingGalleryUrls.includes(f.url))
				.forEach(f => filesToCopy.push(new FileCopyModel().patch({ file: f['@urn'], sourceUrn: currentStandPage.urn, type: standPageImageType?.urn })));

			await Promise.all(filesToCopy);
		} else {
			// 1 - Supprimer les fichiers retirés par l'utilisateur
			const filesToDelete: Promise<unknown>[] = [];

			if (existingLogo && formData.existingLogoUrl === null) {
				filesToDelete.push(new FileModel({ id: getIdFromUrn(existingLogo['@urn']) }).destroy());
			}
			existingGallery
				.filter(f => !formData.existingGalleryUrls.includes(f.url))
				.forEach(f => filesToDelete.push(new FileModel({ id: getIdFromUrn(f['@urn']) }).destroy()));

			await Promise.all(filesToDelete);
		}

		// 2 - Uploader les nouveaux fichiers sélectionnés par l'utilisateur
		const filesToUpload: Promise<unknown>[] = [];

		if (formData.logo) {
			filesToUpload.push(new FileModel().patchFormData({ type: standPageLogoType?.urn, sourceUrn: currentStandPage.urn, file: formData.logo }));
		}
		formData.gallery.forEach(file =>
			filesToUpload.push(new FileModel().patchFormData({ type: standPageImageType?.urn, sourceUrn: currentStandPage.urn, file }))
		);

		await Promise.all(filesToUpload);
	};

	// ─── Rafraîchissement après sauvegarde ──────────────────────────────────────

	const refreshFormFiles = async () => {
		// 1 - Re-sélectionner le standPage à éditer selon la règle métier (celui qu'on vient de créer ou modifier)
		const collection = await new PagedCollection(StandPageModel)
			.setFilter('stand', [stand.id])
			.setSort('id', 'desc')
			.list();
		const fresh = selectStandPageToEdit(collection.models, validationStatuses);
		if (!fresh) return;

		// 2 - Mettre à jour le state et les refs avec les nouvelles données
		setActiveStandPage(fresh);
		existingLogoRef.current    = fresh.getFiles('stand_page_logo').at(0) ?? null;
		existingGalleryRef.current = fresh.getFiles('stand_page_image').slice(0, 3);

		// 3 - Synchroniser le formulaire avec les nouvelles URLs de fichiers
		setValue('existingLogoUrl',    existingLogoRef.current?.url ?? null);
		setValue('existingGalleryUrls', existingGalleryRef.current.map(f => f.url));
		setValue('logo',    null);
		setValue('gallery', []);
	};

	return {
		typeCollectionLoading,
		existingLogoRef,
		existingGalleryRef,
		saveFiles,
		refreshFormFiles,
	};
};
